<template>
  <div class="during-rally-action">
    <h4>During rally</h4>
    <div class="rally-grid">
      <div v-if="anyAssistMode" class="kb-assist-banner">
        {{ blockAssistDecrementMode ? 'Select 2nd blocker to undo assist' : 'Select 2nd blocker for assist' }} ({{ (firstAssistSlot || firstIncrementAssistSlot || firstDecrementAssistSlot)?.name }})
      </div>
      <div
        v-for="slot in playerSlots"
        :key="slot.position"
        class="rally-player"
        :class="{ 'last-clicked': isLastClickedPlayer(slot.number) }"
      >
        <div class="player-header">
          <div class="player-info">
            <span v-if="slot.roleCode" class="player-role">{{ slot.roleCode }}</span>
            <span class="player-name">{{ slot.name }}</span>
          </div>
          <span class="player-number">{{ slot.number }}</span>
        </div>
        <div class="player-content">
          <div class="player-photo-column">
            <img
              v-if="slot.photo"
              class="player-photo"
              :src="getPhotoUrl(slot.photo)"
              :alt="slot.name"
            />
          </div>
          <div class="action-rows">
            <div class="action-row action-row-attack" v-if="slot.canAttack">
              <span class="action-label">Attack</span>
              <button
                class="terminal-inline terminal-kill"
                @click="openKillMenu(slot, $event)"
                :disabled="processingTerminalAction || anyAssistMode"
              >K</button>
              <div class="action-increment">
                <button class="action-btn" @click="adjustCount('attacks', slot.number, -1)" :disabled="anyAssistMode">-</button>
                <span
                  class="action-count"
                  :class="{
                    active: getCount('attacks', slot.number) > 0,
                    last: isLastIncrement('attacks', slot.number)
                  }"
                >
                  {{ getCount('attacks', slot.number) }}
                </span>
                <button class="action-btn" @click="adjustCount('attacks', slot.number, 1)" :disabled="anyAssistMode">+</button>
              </div>
              <button
                class="terminal-inline terminal-error"
                @click="openAttackErrorMenu(slot, $event)"
                :disabled="processingTerminalAction || anyAssistMode"
              >E</button>
            </div>
            <div class="action-row action-row-block" v-if="slot.canBlock">
              <span class="action-label">Block</span>
              <button
                class="terminal-inline terminal-kill"
                @click="handleTerminalAction('killBlockSolo', slot)"
                :disabled="processingTerminalAction || anyAssistMode"
              >KB</button>
              <div class="action-increment">
                <button class="action-btn" @click="adjustCount('blocksSolo', slot.number, -1)" :disabled="anyAssistMode">-</button>
                <span
                  class="action-count"
                  :class="{
                    active: getCount('blocksSolo', slot.number) > 0,
                    last: isLastIncrement('blocksSolo', slot.number)
                  }"
                >
                  {{ getCount('blocksSolo', slot.number) }}
                </span>
                <button class="action-btn" @click="adjustCount('blocksSolo', slot.number, 1)" :disabled="anyAssistMode">+</button>
              </div>
              <button
                class="terminal-inline terminal-error"
                @click="handleTerminalAction('blockError', slot)"
                :disabled="processingTerminalAction || anyAssistMode"
              >E</button>
            </div>
            <div class="action-row action-row-block-assist" v-if="slot.canBlock">
              <span class="action-label">Block (Assist)</span>
              <button
                class="terminal-inline terminal-kill"
                :class="{ 'kb-assist-active': killBlockAssistMode && firstAssistSlot?.number === slot.number }"
                @click="handleTerminalAction('killBlockAssist', slot)"
                :disabled="processingTerminalAction || blockAssistIncrementMode || blockAssistDecrementMode"
              >KB</button>
              <div class="action-increment">
                <button
                  class="action-btn"
                  :class="{ 'kb-assist-active': (blockAssistDecrementMode && firstDecrementAssistSlot?.number === slot.number) || (blockAssistIncrementMode && firstIncrementAssistSlot?.number === slot.number) }"
                  @click="handleBlockAssistDecrement(slot)"
                  :disabled="killBlockAssistMode || (blockAssistIncrementMode && firstIncrementAssistSlot?.number !== slot.number)"
                >-</button>
                <span
                  class="action-count"
                  :class="{
                    'active': getCount('blocksJoint', slot.number) > 0,
                    'last': isLastIncrement('blocksJoint', slot.number),
                    'kb-assist-active': (blockAssistIncrementMode && firstIncrementAssistSlot?.number === slot.number) || (blockAssistDecrementMode && firstDecrementAssistSlot?.number === slot.number)
                  }"
                >
                  {{ getCount('blocksJoint', slot.number) }}
                </span>
                <button
                  class="action-btn"
                  :class="{ 'kb-assist-active': blockAssistIncrementMode && firstIncrementAssistSlot?.number === slot.number }"
                  @click="handleBlockAssistIncrement(slot)"
                  :disabled="killBlockAssistMode || blockAssistDecrementMode || (blockAssistIncrementMode && firstIncrementAssistSlot?.number === slot.number)"
                >+</button>
              </div>
            </div>
            <div class="action-row action-row-dig">
              <span class="action-label">Dig</span>
              <div class="action-increment">
                <button class="action-btn" @click="adjustCount('digs', slot.number, -1)" :disabled="anyAssistMode">-</button>
                <span
                  class="action-count"
                  :class="{
                    active: getCount('digs', slot.number) > 0,
                    last: isLastIncrement('digs', slot.number)
                  }"
                >
                  {{ getCount('digs', slot.number) }}
                </span>
                <button class="action-btn" @click="adjustCount('digs', slot.number, 1)" :disabled="anyAssistMode">+</button>
              </div>
            </div>
            <div class="action-row action-row-other" v-if="slot.canAttack">
              <span class="action-label">Other</span>
              <button
                class="terminal-inline terminal-error"
                @click="handleTerminalAction('otherError', slot)"
                :disabled="processingTerminalAction || anyAssistMode"
              >E</button>
            </div>
          </div>
        </div>
      </div>
      <div class="rally-player rally-team">
        <div class="player-header">
          <div class="player-info">
            <span class="player-name">Team</span>
          </div>
        </div>

        <div class="player-content">
          <div class="action-rows">
            <div class="action-row action-row-other">
              <span class="action-label">Other</span>
              <button
                class="terminal-inline terminal-error"
                @click="handleOpponentAction('teamOtherError')"
                :disabled="processingTerminalAction || anyAssistMode"
              >E</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="opponent-actions">
      <div class="opponent-grid">
        <button
          class="terminal-btn terminal-won"
          @click="handleOpponentAction('oppAttackError')"
          :disabled="processingTerminalAction || anyAssistMode"
        >
          Opposition Attack Error
        </button>
        <button
          class="terminal-btn terminal-won"
          @click="handleOpponentAction('oppBlockError')"
          :disabled="processingTerminalAction || anyAssistMode"
        >
          Opposition Block Error
        </button>
        <button
          class="terminal-btn terminal-won"
          @click="handleOpponentAction('oppOtherError')"
          :disabled="processingTerminalAction || anyAssistMode"
        >
          Opposition Other Error
        </button>
        <button
          class="terminal-btn terminal-lost"
          @click="handleOpponentAction('teamOtherError')"
          :disabled="processingTerminalAction || anyAssistMode"
        >
          Team Other Error
        </button>
        <button
          class="terminal-btn terminal-lost"
          @click="handleOpponentAction('oppKill')"
          :disabled="processingTerminalAction || anyAssistMode"
        >
          Opposition Kill
        </button>
      </div>
    </div>
    <!-- Attack subtype context menu -->
    <div v-if="activeContextMenu" class="context-backdrop" @click="dismissContextMenu">
      <div class="attack-context-menu" :style="contextMenuStyle" @click.stop>
        <template v-if="activeContextMenu.type === 'kill'">
          <button
            v-for="opt in killOptions"
            :key="opt.value"
            class="context-option context-option-kill"
            @click="selectKillSubtype(opt.value)"
          >{{ opt.label }}</button>
        </template>
        <template v-if="activeContextMenu.type === 'attackError'">
          <button
            v-for="opt in errorOptions"
            :key="opt.value"
            class="context-option context-option-error"
            @click="selectAttackErrorSubtype(opt.value)"
          >{{ opt.label }}</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';
export default {
  name: 'DuringRallyAction',
  props: {
    currentPoint: {
      type: Object,
      required: true
    },
    currentLineup: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      killBlockAssistMode: false,
      firstAssistPlayer: null,
      firstAssistSlot: null,
      blockAssistIncrementMode: false,
      firstIncrementAssistSlot: null,
      blockAssistDecrementMode: false,
      firstDecrementAssistSlot: null,
      processingTerminalAction: false,
      activeContextMenu: null
    };
  },
  computed: {
    playerSlots() {
      const positions = this.currentPoint.currentOnCourtNumbers || [];
      const liberoNumber = this.currentPoint.currentLiberoNumber || null;
      const liberoPlayer = store.currentLibero && store.currentLibero.number === liberoNumber
        ? store.currentLibero
        : null;
      const playersByNumber = new Map(
        this.currentLineup
          .filter(item => item.player && item.player.number !== undefined)
          .map(item => [item.player.number, item.player])
      );
      if (liberoPlayer && liberoPlayer.number !== undefined) {
        playersByNumber.set(liberoPlayer.number, liberoPlayer);
      }

      const frontRowPositions = [4, 3, 2];
      const backRowPositions = [5, 6, 1];

      const findNumberByRole = (positionIds, roles) => {
        const candidates = positionIds.map(pos => positions[pos - 1]).filter(Boolean);
        return candidates.find(number => roles.includes(this.getRoleCode(number))) || null;
      };

      const frontOh = findNumberByRole(frontRowPositions, ['OH']);
      const frontMb = findNumberByRole(frontRowPositions, ['MB']);
      const frontOpOrS = findNumberByRole(frontRowPositions, ['OP', 'S']);

      const backRowNumbers = backRowPositions
        .map(pos => positions[pos - 1])
        .filter(Boolean);
      const pickByRole = (roles, exclude = []) =>
        backRowNumbers.find(number => !exclude.includes(number) && roles.includes(this.getRoleCode(number))) || null;
      const backOh = pickByRole(['OH']);
      const backOpOrS = pickByRole(['OP', 'S'], backOh ? [backOh] : []);
      const remainingBack = backRowNumbers.find(number => ![backOh, backOpOrS].includes(number)) || null;

      const receivingInP1 = this.currentPoint.startPoint === 'Receive' && this.currentPoint.currentRotationCode === 'P1';
      const frontLeft = receivingInP1 ? frontOpOrS : frontOh;
      const frontRight = receivingInP1 ? frontOh : frontOpOrS;

      const displayNumbers = {
        4: frontLeft || positions[3],
        3: frontMb || positions[2],
        2: frontRight || positions[1],
        5: (liberoNumber && positions[4] === liberoNumber) ? liberoNumber : (remainingBack || positions[4]),
        6: backOh || positions[5],
        1: backOpOrS || positions[0]
      };

      const order = [4, 3, 2, 5, 6, 1];
      return order.map(position => {
        const number = displayNumbers[position];
        const player = playersByNumber.get(number) || {};
        const roleCode = this.getRoleCode(number);
        const isLibero = liberoNumber && number === liberoNumber;
        const isBackRow = [1, 5, 6].includes(position);
        return {
          position,
          number: number ?? '',
          name: player.name || '',
          photo: player.photo || '',
          roleCode,
          canBlock: !isBackRow && !isLibero,
          canAttack: !isLibero
        };
      });
    },
    killOptions() {
      return [
        { label: 'Attack', value: 'attack' },
        { label: 'Tip', value: 'tip' },
        { label: 'Roll', value: 'roll' },
        { label: 'Volley', value: 'volley' },
        { label: 'Dump', value: 'dump' },
        { label: 'Other', value: 'other' }
      ];
    },
    errorOptions() {
      return [
        { label: 'Net', value: 'net' },
        { label: 'Out', value: 'out' },
        { label: 'Blocked', value: 'block' },
        { label: 'Other', value: 'other' }
      ];
    },
    contextMenuStyle() {
      if (!this.activeContextMenu || !this.activeContextMenu.rect) {
        return {};
      }
      const rect = this.activeContextMenu.rect;
      const menuWidth = 240;
      let left = rect.left + rect.width / 2 - menuWidth / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
      const top = rect.bottom + 8;
      return {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${menuWidth}px`
      };
    },
    anyAssistMode() {
      return this.killBlockAssistMode || this.blockAssistIncrementMode || this.blockAssistDecrementMode;
    }
  },
  methods: {
    getPhotoUrl(photo) {
      try {
        if (photo) {
          return require(`@/assets/${photo}`);
        }
        return require('@/assets/player-photos/placeholder.png');
      } catch (error) {
        return require('@/assets/player-photos/placeholder.png');
      }
    },
    getRoleCode(number) {
      const found = this.currentLineup.find(item => item.player && item.player.number === number);
      if (found && found.roleCode) return found.roleCode;
      if (store.currentLibero && store.currentLibero.number === number) return 'L';
      return '';
    },
    getCount(type, number) {
      const bucket = this.currentPoint.duringRally?.[type] || {};
      return bucket[number] || 0;
    },
    isLastIncrement(type, number) {
      if (!number) {
        return false;
      }
      const events = this.currentPoint.duringRallyEvents || [];
      if (!events.length) {
        return false;
      }
      const lastEvent = events[events.length - 1];
      return lastEvent.type === type && lastEvent.number === number;
    },
    isLastClickedPlayer(number) {
      if (!number) {
        return false;
      }
      const events = this.currentPoint.duringRallyEvents || [];
      if (!events.length) {
        return false;
      }
      const lastEvent = events[events.length - 1];
      return lastEvent.number === number;
    },
    adjustCount(type, number, delta) {
      if (!number || this.anyAssistMode) {
        return;
      }
      const next = {
        attacks: { ...(this.currentPoint.duringRally?.attacks || {}) },
        blocksSolo: { ...(this.currentPoint.duringRally?.blocksSolo || {}) },
        blocksJoint: { ...(this.currentPoint.duringRally?.blocksJoint || {}) },
        digs: { ...(this.currentPoint.duringRally?.digs || {}) }
      };
      const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
      const current = next[type][number] || 0;
      const updated = Math.max(0, current + delta);
      if (updated === 0) {
        delete next[type][number];
      } else {
        next[type][number] = updated;
      }
      if (delta > 0) {
        nextEvents.push({ type, number });
      } else if (delta < 0) {
        const lastIndex = [...nextEvents].reverse().findIndex(event => event.type === type && event.number === number);
        if (lastIndex !== -1) {
          const indexToRemove = nextEvents.length - 1 - lastIndex;
          nextEvents.splice(indexToRemove, 1);
        }
      }
      this.$emit('update-during-rally', { counts: next, events: nextEvents });
    },
    handleTerminalAction(actionType, slot) {
      if (this.processingTerminalAction || !slot.number) {
        return;
      }

      // When in any assist mode, only allow killBlockAssist actions (for terminal)
      if (this.anyAssistMode && actionType !== 'killBlockAssist') {
        return;
      }

      const payload = this.buildTerminalActionPayload(actionType, slot);
      if (!payload) {
        // Kill block assist first click - don't emit yet
        return;
      }

      this.processingTerminalAction = true;
      this.$emit('terminal-action', payload);

      // Reset processing flag after short delay
      setTimeout(() => {
        this.processingTerminalAction = false;
      }, 500);
    },
    buildTerminalActionPayload(actionType, slot) {
      const actionMap = {
        'kill': { outcome: 'Won', pointDetail: 'Kill' },
        'attackError': { outcome: 'Lost', pointDetail: 'Attack error' },
        'killBlockSolo': { outcome: 'Won', pointDetail: 'Kill block (solo)' },
        'killBlockAssist': { outcome: 'Won', pointDetail: 'Kill block (assist)', twoPlayers: true },
        'blockError': { outcome: 'Lost', pointDetail: 'Block error' },
        'otherError': { outcome: 'Lost', pointDetail: 'Other error' }
      };

      const config = actionMap[actionType];
      if (!config) return null;

      // Handle kill block assist first click BEFORE player validation
      // (getPlayerFromSlot may return null due to lineup/number mismatch)
      if (config.twoPlayers && !this.killBlockAssistMode) {
        this.killBlockAssistMode = true;
        this.firstAssistSlot = slot;
        return null;
      }

      // Get actual player object from currentLineup
      const player = this.getPlayerFromSlot(slot);
      if (!player || !player.number) return null;

      // Handle kill block assist second click
      if (config.twoPlayers) {
        const firstPlayer = this.getPlayerFromSlot(this.firstAssistSlot);
        if (!firstPlayer || !firstPlayer.number) {
          this.resetKillBlockAssistMode();
          return null;
        }
        if (firstPlayer.number === player.number) {
          // Same player clicked - cancel assist mode
          this.resetKillBlockAssistMode();
          return null;
        }

        const payload = {
          outcome: config.outcome,
          pointDetail: config.pointDetail,
          player: firstPlayer,
          secondPlayer: player
        };

        this.resetKillBlockAssistMode();
        return payload;
      }

      // Regular terminal action
      return {
        outcome: config.outcome,
        pointDetail: config.pointDetail,
        player: player,
        secondPlayer: null
      };
    },
    handleOpponentAction(actionType) {
      if (this.processingTerminalAction || this.anyAssistMode) {
        return;
      }

      const opponentActionMap = {
        'oppAttackError': { outcome: 'Won', pointDetail: 'Opp. attack error' },
        'oppBlockError': { outcome: 'Won', pointDetail: 'Opp. block error' },
        'oppServeError': { outcome: 'Won', pointDetail: 'Opp. serve error' },
        'oppOtherError': { outcome: 'Won', pointDetail: 'Opp. other error' },
        'teamOtherError': { outcome: 'Lost', pointDetail: 'Other error' },
        'oppKill': { outcome: 'Lost', pointDetail: 'Opp. kill' }
      };

      const config = opponentActionMap[actionType];
      if (!config) return;

      this.processingTerminalAction = true;
      this.$emit('terminal-action', {
        outcome: config.outcome,
        pointDetail: config.pointDetail,
        player: actionType === 'teamOtherError' ? 'Team' : {},
        secondPlayer: null
      });

      setTimeout(() => {
        this.processingTerminalAction = false;
      }, 500);
    },
    resetKillBlockAssistMode() {
      this.killBlockAssistMode = false;
      this.firstAssistPlayer = null;
      this.firstAssistSlot = null;
    },
    handleBlockAssistIncrement(slot) {
      if (this.killBlockAssistMode || !slot.number) return;

      if (!this.blockAssistIncrementMode) {
        // First click — increment first player immediately, enter assist mode
        const next = this.cloneDuringRally();
        const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
        next.blocksJoint[slot.number] = (next.blocksJoint[slot.number] || 0) + 1;
        nextEvents.push({ type: 'blocksJoint', number: slot.number });
        this.$emit('update-during-rally', { counts: next, events: nextEvents });

        this.blockAssistIncrementMode = true;
        this.firstIncrementAssistSlot = slot;
        return;
      }

      // Second click
      const firstNumber = this.firstIncrementAssistSlot.number;
      if (firstNumber === slot.number) {
        // Same player — cancel (undo the first increment)
        this.cancelBlockAssistIncrement();
        return;
      }

      // Different player — increment second player
      const next = this.cloneDuringRally();
      const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
      next.blocksJoint[slot.number] = (next.blocksJoint[slot.number] || 0) + 1;
      nextEvents.push({ type: 'blocksJoint', number: slot.number });

      this.resetBlockAssistIncrementMode();
      this.$emit('update-during-rally', { counts: next, events: nextEvents });
    },
    handleBlockAssistDecrement(slot) {
      if (this.killBlockAssistMode || !slot.number) return;

      // If in increment mode, only allow decrement on the first selected player (to cancel)
      if (this.blockAssistIncrementMode) {
        if (this.firstIncrementAssistSlot?.number === slot.number) {
          this.cancelBlockAssistIncrement();
        }
        return;
      }

      if (!this.blockAssistDecrementMode) {
        // First click — decrement first player immediately, enter assist mode
        this.decrementOnePlayer(slot.number);
        this.blockAssistDecrementMode = true;
        this.firstDecrementAssistSlot = slot;
        return;
      }

      // Second click
      const firstNumber = this.firstDecrementAssistSlot.number;
      if (firstNumber === slot.number) {
        // Same player — cancel (re-increment to undo)
        const next = this.cloneDuringRally();
        const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
        next.blocksJoint[firstNumber] = (next.blocksJoint[firstNumber] || 0) + 1;
        nextEvents.push({ type: 'blocksJoint', number: firstNumber });
        this.$emit('update-during-rally', { counts: next, events: nextEvents });
        this.resetBlockAssistDecrementMode();
        return;
      }

      // Different player — decrement second player
      this.decrementOnePlayer(slot.number);
      this.resetBlockAssistDecrementMode();
    },
    decrementOnePlayer(number) {
      const next = this.cloneDuringRally();
      const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
      const current = next.blocksJoint[number] || 0;
      if (current <= 1) {
        delete next.blocksJoint[number];
      } else {
        next.blocksJoint[number] = current - 1;
      }
      const lastIdx = [...nextEvents].reverse().findIndex(e => e.type === 'blocksJoint' && e.number === number);
      if (lastIdx !== -1) {
        nextEvents.splice(nextEvents.length - 1 - lastIdx, 1);
      }
      this.$emit('update-during-rally', { counts: next, events: nextEvents });
    },
    resetBlockAssistDecrementMode() {
      this.blockAssistDecrementMode = false;
      this.firstDecrementAssistSlot = null;
    },
    cancelBlockAssistIncrement() {
      const firstNumber = this.firstIncrementAssistSlot.number;
      const next = this.cloneDuringRally();
      const nextEvents = [...(this.currentPoint.duringRallyEvents || [])];
      const current = next.blocksJoint[firstNumber] || 0;
      if (current <= 1) {
        delete next.blocksJoint[firstNumber];
      } else {
        next.blocksJoint[firstNumber] = current - 1;
      }
      const lastIndex = [...nextEvents].reverse().findIndex(e => e.type === 'blocksJoint' && e.number === firstNumber);
      if (lastIndex !== -1) {
        nextEvents.splice(nextEvents.length - 1 - lastIndex, 1);
      }
      this.resetBlockAssistIncrementMode();
      this.$emit('update-during-rally', { counts: next, events: nextEvents });
    },
    cloneDuringRally() {
      return {
        attacks: { ...(this.currentPoint.duringRally?.attacks || {}) },
        blocksSolo: { ...(this.currentPoint.duringRally?.blocksSolo || {}) },
        blocksJoint: { ...(this.currentPoint.duringRally?.blocksJoint || {}) },
        digs: { ...(this.currentPoint.duringRally?.digs || {}) }
      };
    },
    resetBlockAssistIncrementMode() {
      this.blockAssistIncrementMode = false;
      this.firstIncrementAssistSlot = null;
    },
    openKillMenu(slot, event) {
      if (this.processingTerminalAction || this.anyAssistMode || !slot.number) return;
      const rect = event.target.getBoundingClientRect();
      this.activeContextMenu = { type: 'kill', slot, rect };
    },
    openAttackErrorMenu(slot, event) {
      if (this.processingTerminalAction || this.anyAssistMode || !slot.number) return;
      const rect = event.target.getBoundingClientRect();
      this.activeContextMenu = { type: 'attackError', slot, rect };
    },
    dismissContextMenu() {
      this.activeContextMenu = null;
    },
    selectKillSubtype(subtype) {
      const slot = this.activeContextMenu.slot;
      const player = this.getPlayerFromSlot(slot);
      if (!player || !player.number) {
        this.activeContextMenu = null;
        return;
      }
      this.activeContextMenu = null;
      this.processingTerminalAction = true;
      this.$emit('terminal-action', {
        outcome: 'Won',
        pointDetail: 'Kill',
        player,
        secondPlayer: null,
        attackSubtype: subtype
      });
      setTimeout(() => { this.processingTerminalAction = false; }, 500);
    },
    selectAttackErrorSubtype(subtype) {
      const slot = this.activeContextMenu.slot;
      const player = this.getPlayerFromSlot(slot);
      if (!player || !player.number) {
        this.activeContextMenu = null;
        return;
      }
      this.activeContextMenu = null;
      this.processingTerminalAction = true;
      const isBlock = subtype === 'block';
      this.$emit('terminal-action', {
        outcome: 'Lost',
        pointDetail: isBlock ? 'Opp. kill block' : 'Attack error',
        player,
        secondPlayer: null,
        attackErrorSubtype: subtype
      });
      setTimeout(() => { this.processingTerminalAction = false; }, 500);
    },
    getPlayerFromSlot(slot) {
      // Find actual player object from currentLineup by number
      const found = this.currentLineup.find(
        item => item.player && item.player.number === slot.number
      );
      if (found) return found.player;
      // Fallback to current libero (not in rotation lineup)
      if (store.currentLibero && store.currentLibero.number === slot.number) {
        return store.currentLibero;
      }
      return null;
    }
  }
};
</script>

<style scoped lang="scss">
.during-rally-action {
  padding: 0 10px;
  font-size: 12px;

  h4 {
    margin: 0 0 12px 0;
    font-size: 24px;
  }
}

.rally-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(200px, 600px));
  gap: 10px;
  justify-content: center;
}

.rally-player {
  background: var(--grey-5);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 260px;
  color: var(--grey-900);

  &.rally-team {
    min-height: 56px;
    grid-column: 2 / 3;
  }
}

.player-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px 8px;
  align-items: flex-start;
  z-index: 2;
}

.player-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;

  .player-role {
    font-size: 10px;
    font-weight: 700;
    color: var(--grey-500);
    text-transform: uppercase;
    background-color: var(--grey-50);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .player-name {
    font-weight: 600;
    font-size: 16px;
    color: var(--grey-800);
  }
}

.player-number {
  font-weight: 800;
  font-size: 40px;
  line-height: 1;
  color: var(--red-300);
}

.player-content {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  position: relative;
}

.player-photo-column {
  flex-grow: 1;
  overflow: hidden;
  position: absolute;
  left: 0;
  z-index: 1;
  max-width: 160px;
  width: 40%;

  .player-photo {
    width: 100%;
    max-width: 200px;
    height: auto;
    object-fit: cover;
    display: flex;
    transform: translateX(-25%);
  }
}

.action-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  flex-shrink: 0;
  z-index: 2;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 6px;

  &.action-row-block-assist,
  &.action-row-dig {
    margin-right: 36px;
  }

  .action-label {
    font-weight: 600;
    color: var(--grey-800);
    flex-basis: 100%;
    text-align: right;
    white-space: nowrap;
  }

  .action-increment {
    display: flex;
    align-items: center;
    gap: 0;
    background: var(--grey-50);
    border-radius: 100px;
    padding: 0 2px;

    &:has(.active) {
      background: var(--grey-900);
    }

    .action-count {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      color: var(--grey-800);
      border: 2px solid transparent;

      &.active {
        color: var(--white);
      }
    }

    .action-btn {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      border: none;
      background: var(--white);
      font-weight: 500;
      cursor: pointer;
      padding: 0;
      font-size: 16px;
      color: var(--grey-800);
      flex-shrink: 0;

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }
}
.terminal-inline {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    transform: scale(0.95);
  }
}

.terminal-inline.terminal-kill {
  background: var(--green-100);
  color: var(--green-700);

  &:not(:disabled):hover {
    background: var(--green-300);
    color: var(--green-900);
  }
}

.terminal-inline.terminal-error {
  background: var(--red-50);
  color: var(--red-400);

  &:not(:disabled):hover {
    background: var(--red-75);
    color: var(--red-600);
  }
}

.terminal-inline.kb-assist-active {
  background: var(--green-600);
  box-shadow: 0 0 0 3px var(--green-100);
}

.rally-player.last-clicked {
  background: var(--grey-900);
  color: var(--white);

  .action-increment {
    background: var(--grey-600);

    &:has(.active) {
      background: var(--grey-200);

      &:has(.last) {
        background: var(--white);
        border: 2px solid var(--grey-900);
        margin: -2px;
        box-shadow: 0 0 0 2px var(--white);
        color: var(--grey-900);
        z-index: 4;
      }
    }

    .active {
      color: var(--grey-900);
    }
  }
  
  .action-btn {
    background: var(--grey-900);
    color: var(--white);
  }

  .player-info {
    .player-role {
      background: var(--grey-700);
      color: var(--white);
    }

    .player-name {
      color: var(--white);
    }
  }

  .action-label {
    color: var(--white);
  }

  .action-count {
    color: var(--white);
  }
}

.kb-assist-banner {
  grid-column: 1 / -1;
  background: var(--green-100);
  padding: 10px;
  text-align: center;
  font-weight: 600;
  color: var(--grey-900);
  border-radius: 6px;
  margin-bottom: 8px;
  border: 2px solid var(--green-300);
}

.terminal-btn {
  flex: 1;
  padding: 20px 10px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.terminal-btn.terminal-won {
  background: var(--green-100);
  color: var(--green-700);

  &:not(:disabled):hover {
    background: var(--green-200);
  }
}

.terminal-btn.terminal-lost {
  background: var(--red-50);
  color: var(--red-400);

  &:not(:disabled):hover {
    background: var(--red-100);
  }
}

.opponent-actions {
  margin-top: 10px;
}

.opponent-grid {
  display: flex;
  gap: 8px;
  margin: 0 auto;
}

.context-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 199;
  background: rgba(0, 0, 0, 0.15);
}

.attack-context-menu {
  z-index: 200;
  background: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.context-option {
  min-height: 44px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 4px;
  transition: all 0.15s ease;

  &:active {
    transform: scale(0.95);
  }
}

.context-option-kill {
  background: var(--green-100);
  color: var(--green-700);

  &:hover {
    background: var(--green-200);
  }
}

.context-option-error {
  background: var(--red-50);
  color: var(--red-400);

  &:hover {
    background: var(--red-75);
  }
}

</style>
